/**
 * Port of `brouter-web-master/js/control/TrackStats.js`.
 *
 * The engine returns per-leg totals as strings in the GeoJSON feature properties;
 * these are summed across legs and then formatted exactly as the legacy footer did.
 */

import { BRouterSegment, TrackStats } from '../models/brouter-map.model';
import { formatNum } from './brouter-url';

export interface RawTrackTotals {
  /** Metres. */
  trackLength: number;
  /** Metres, smoothed by the engine. */
  filteredAscend: number;
  /** Metres, raw sample-to-sample sum. */
  plainAscend: number;
  /** Seconds. */
  totalTime: number;
  /** Joules. */
  totalEnergy: number;
  cost: number;
}

const emptyTotals = (): RawTrackTotals => ({
  trackLength: 0,
  filteredAscend: 0,
  plainAscend: 0,
  totalTime: 0,
  totalEnergy: 0,
  cost: 0
});

/** Coerces the engine's stringly-typed properties, treating absent values as zero. */
const num = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const calcTotals = (segments: readonly (BRouterSegment | null)[]): RawTrackTotals => {
  const totals = emptyTotals();

  for (const segment of segments) {
    if (!segment) {
      continue;
    }
    const props = segment.properties;
    totals.trackLength += num(props['track-length']);
    totals.filteredAscend += num(props['filtered ascend']);
    totals.plainAscend += num(props['plain-ascend']);
    totals.totalTime += num(props['total-time']);
    totals.totalEnergy += num(props['total-energy']);
    totals.cost += num(props['cost']);
  }

  return totals;
};

export const calcTrackStats = (
  segments: readonly (BRouterSegment | null)[],
  beelineFlags: readonly boolean[] = []
): TrackStats => {
  const totals = calcTotals(segments);
  return {
    distance: totals.trackLength,
    totalTime: totals.totalTime,
    totalEnergy: totals.totalEnergy,
    ascend: totals.filteredAscend,
    plainAscend: totals.plainAscend,
    cost: totals.cost,
    meanCostFactor: totals.trackLength ? formatNum(totals.cost / totals.trackLength, 2) : 0,
    hasBeeline: beelineFlags.some(Boolean)
  };
};

/** Kilometres to one decimal, as shown in the footer. */
export const formatDistanceKm = (metres: number): number => formatNum(metres / 1000, 1);

/** Kilometres to three decimals, used for the footer tooltip. */
export const formatDistanceKmPrecise = (metres: number): number => formatNum(metres / 1000, 3);

/** `H:MM`, matching the legacy footer. Hours are not wrapped at 24. */
export const formatDuration = (seconds: number): string => {
  const hours = Math.trunc(seconds / 3600);
  const minutes = `0${Math.trunc((seconds % 3600) / 60)}`.slice(-2);
  return `${hours}:${minutes}`;
};

/** `H:MM:SS`, used for the footer tooltip. */
export const formatDurationWithSeconds = (seconds: number): string =>
  `${formatDuration(seconds)}:${`0${Math.trunc(seconds % 60)}`.slice(-2)}`;

/** Joules to kilowatt hours, to two decimals. */
export const formatEnergyKWh = (joules: number): number => formatNum(joules / 3600000, 2);

/** Mean energy in watt hours per kilometre, to two decimals. */
export const formatMeanEnergy = (joules: number, metres: number): number =>
  metres ? formatNum(joules / 36 / metres, 2) : 0;
